/* Back-in-stock sign-up (snippets/fi-notify) without a page reload.
   The form is a Shopify customer form: posting it creates (or finds) a customer
   tagged bis-<variant id>,notify-me, and a Shopify Flow workflow emails that customer
   when the variant is back in stock. A customer form never emails the shop. It is
   sent in the background with fetch (urlencoded, the way the browser would send it)
   and Shopify's answer is read:
   - a redirect to ?customer_posted=true: the popover's content becomes the thanks;
   - Shopify's error list for this form: shown in the popover;
   - anything else (a refused captcha, a 429 from Cloudflare): a "try again" note.

   Captcha: Shopify's storefront hCaptcha script (storefront-forms-hcaptcha) binds to
   /contact forms. When it binds it keeps the form's own submit function
   (form.submit, if the form has one, else HTMLFormElement.prototype.submit); on
   submit it cancels the post, runs invisible hCaptcha, writes the token into
   h-captcha-response and then calls that kept function. /contact refuses a post
   without a valid, unused token. So each notify form gets its own form.submit,
   set before the captcha script binds, that does the background send: Shopify
   calls it with the token in place and the page stays put.
   Without that script nothing cancels the submit, and the window listener at the
   end sends the form itself.

   Variant: the product page's buy column is not re-rendered on a variant change, so
   the tag is rewritten here on every variantChange (product-info.js), the notify
   buttons are shown only while the selected variant is sold out, and Add to basket
   takes their place otherwise. A form whose tag does not match the variant picked in
   the product form is never sent.

   The popover sits in the browser's top layer, above hCaptcha's challenge frame. If
   hCaptcha asks for a visible challenge, the popover is closed so the shopper can
   answer it, and opened again with the result.
   Loaded once, from layout/theme.liquid. */
(function () {
  'use strict';

  if (!window.fetch) return;

  var THANKS = "Thanks — we'll email you when it's back in stock.";
  var FAILED = 'Sorry, something went wrong. Please try again.';
  var IN_STOCK = 'This option is in stock — you can order it now.';
  var RESELECT = 'Please choose your options again, then try once more.';
  var SENDING = 'Sending…';
  var nativeSubmit = HTMLFormElement.prototype.submit;

  function parts(form) {
    var panel = form.querySelector('[popover], [role="dialog"]') || form;
    var note = panel.querySelector('.fi-notify__msg');
    if (!note) {
      note = document.createElement('p');
      note.className = 'fi-notify__msg';
      note.setAttribute('role', 'status');
      panel.appendChild(note);
    }
    return { panel: panel, note: note };
  }

  function popoverOf(form) {
    var pop = form.querySelector('[popover]');
    return pop && typeof pop.showPopover === 'function' ? pop : null;
  }

  function reopen(form) {
    var pop = popoverOf(form);
    if (!pop || pop.matches(':popover-open')) return;
    try {
      pop.showPopover();
    } catch (error) {}
  }

  /* hCaptcha keeps an invisible challenge frame off-screen; it only counts once it
     is actually on screen. */
  function challengeShowing() {
    var frames = document.querySelectorAll('iframe[src*="hcaptcha"][src*="challenge"]');
    for (var i = 0; i < frames.length; i++) {
      var rect = frames[i].getBoundingClientRect();
      if (rect.width < 50 || rect.bottom <= 0 || rect.top >= window.innerHeight) continue;
      var visible = frames[i].checkVisibility
        ? frames[i].checkVisibility({ visibilityProperty: true, opacityProperty: true })
        : true;
      if (visible) return true;
    }
    return false;
  }

  /* While the token is being fetched: close the popover if a challenge shows up. */
  function watchChallenge(form) {
    if (form._fiWatch) return;
    var started = Date.now();
    form._fiWatch = setInterval(function () {
      if (!form.dataset.fiPending || Date.now() - started > 120000) {
        clearInterval(form._fiWatch);
        form._fiWatch = null;
        return;
      }
      var pop = popoverOf(form);
      if (pop && pop.matches(':popover-open') && challengeShowing()) {
        form.dataset.fiHidden = '1';
        pop.hidePopover();
      }
    }, 300);
  }

  function show(form, text, success) {
    var p = parts(form);
    if (success) {
      /* style, not [hidden]: .button--full-width sets display and would win. */
      Array.prototype.forEach.call(p.panel.children, function (child) {
        if (child !== p.note && !child.classList.contains('fi-notify__x')) child.style.display = 'none';
      });
    }
    p.note.textContent = text;
    if (form.dataset.fiHidden) {
      delete form.dataset.fiHidden;
      reopen(form);
    }
  }

  /* Back to an empty form, for a new variant after a sign-up for the last one. */
  function reset(form) {
    if (form.dataset.fiBusy) return;
    var p = parts(form);
    Array.prototype.forEach.call(p.panel.children, function (child) {
      if (child !== p.note) child.style.display = '';
    });
    p.note.textContent = '';
  }

  /* Why this form must not be sent, or '' when it can be. The tag is only filled in
     for a sold-out variant, and on the product page it has to be for the variant the
     product form holds right now (blank there means an option combination that does
     not exist). Cards have no product form beside them and carry their one variant. */
  function refusal(form) {
    var tags = form.elements['contact[tags]'];
    if (!tags || !/^bis-\d+,notify-me$/.test(tags.value)) return IN_STOCK;
    var picked = form.parentElement && form.parentElement.querySelector('product-form [name="id"]');
    if (picked && picked.value !== form.dataset.fiVariant) return RESELECT;
    return '';
  }

  function send(form) {
    if (form.dataset.fiBusy) return;
    delete form.dataset.fiPending;
    var refused = refusal(form);
    if (refused) return show(form, refused);
    form.dataset.fiBusy = '1';
    var submit = form.querySelector('[type="submit"]');
    if (submit) submit.disabled = true;
    parts(form).note.textContent = SENDING;

    toShop(form).then(function (shop) {
      delete form.dataset.fiBusy;
      if (submit) submit.disabled = false;
      if (shop.sent) return show(form, THANKS, true);
      if (shop.error) return show(form, shop.error);
      if (shop.challenge) {
        /* Shopify's spam check holds the sign-up until a person answers it on the
           /challenge page, which only works as a real page. So post the form the
           normal way: Shopify's captcha script runs again for a fresh token, then the
           browser goes to the check and on to the saved sign-up. */
        console.warn('[fi-notify] held by Shopify spam check, posting normally:', shop.challenge);
        parts(form).note.textContent = 'One more security check…';
        form.dataset.fiNative = '1';
        if (form.requestSubmit) return form.requestSubmit(form.querySelector('[type="submit"]') || undefined);
        return nativeSubmit.call(form);
      }
      show(form, FAILED);
    });
  }

  /* The customer form post. The redirect is followed and its target read: only
     ?customer_posted=true is a saved sign-up. Shopify's spam check also answers with
     a redirect, to /challenge, and that sign-up is not saved until it is answered. */
  function toShop(form) {
    return fetch(form.action.split('#')[0], {
      method: 'POST',
      body: new URLSearchParams(new FormData(form)),
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (/[?&]customer_posted=true/.test(response.url)) return { sent: true };
        if (response.url.indexOf('/challenge') !== -1) return { challenge: response.url };
        return response.text().then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var same = form.id ? doc.getElementById(form.id) : null;
          var errors = same && same.querySelector('.errors, [role="alert"]');
          if (errors && errors.textContent.trim()) return { error: errors.textContent.trim() };
          if (response.ok && same && same.querySelector('[role="status"]')) return { sent: true };
          console.warn('[fi-notify] sign-up not saved:', response.status, doc.title || html.slice(0, 200));
          return {};
        });
      })
      .catch(function (error) {
        console.warn('[fi-notify] sign-up request failed:', error);
        return {};
      });
  }

  /* A variant change on the product page (product-info.js). Every notify form whose
     product has that variant gets its tag rewritten; its buttons, and Add to basket
     in the same buy column, follow the variant's availability. */
  function setVariant(form, variant) {
    var id = String(variant.id);
    var available = !!variant.available;
    var tags = form.elements['contact[tags]'];
    form.dataset.fiVariant = available ? '' : id;
    if (tags) tags.value = available ? '' : 'bis-' + id + ',notify-me';
    reset(form);

    var pop = form.querySelector('[popover]');
    if (!pop) return;
    if (available && popoverOf(form)) {
      try {
        if (pop.matches(':popover-open')) pop.hidePopover();
      } catch (error) {}
    }
    Array.prototype.forEach.call(
      document.querySelectorAll('.fi-notify-btn[popovertarget="' + pop.id + '"]'),
      function (button) {
        button.hidden = available;
      }
    );
    var add = form.parentElement && form.parentElement.querySelector('product-form [name="add"]');
    if (add) add.style.display = available ? '' : 'none';
  }

  function onVariantChange(event) {
    var variant = event && event.data && event.data.variant;
    if (!variant || variant.id == null) return;
    var id = ',' + variant.id + ',';
    Array.prototype.forEach.call(document.querySelectorAll('form.fi-notify[data-fi-variants]'), function (form) {
      if ((',' + form.dataset.fiVariants + ',').indexOf(id) !== -1) setVariant(form, variant);
    });
  }

  /* constants.js and pubsub.js load before this file; the fallback is for a page
     that reorders them. */
  function listen() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    return true;
  }
  if (!listen()) document.addEventListener('DOMContentLoaded', listen);

  /* Own submit function on the form, before Shopify's captcha script binds to it. */
  function prepare(form) {
    if (form._fiPrepared) return;
    form._fiPrepared = true;
    try {
      form.submit = function () {
        if (form.dataset.fiNative) return nativeSubmit.call(form);
        send(form);
      };
    } catch (error) {}
  }

  /* Shopify only loads its captcha (two scripts, then the widget) on the first
     submit, which made "Sending…" take several seconds. Shopify.captcha.protect is
     the loader's own way to bind a form ahead of time, so it is called as soon as
     the shopper heads for the notify button; by the time they submit, only the
     captcha check itself is left. */
  function warm(form) {
    if (!form || form._fiWarm) return;
    prepare(form);
    var captcha = window.Shopify && window.Shopify.captcha;
    if (!captcha || typeof captcha.protect !== 'function') return;
    form._fiWarm = true;
    try {
      captcha.protect(form);
    } catch (error) {}
  }

  function formFor(target) {
    var el = target && target.closest && target.closest('.fi-notify-btn, form.fi-notify');
    if (!el) return null;
    if (el.matches('form')) return el;
    var pop = document.getElementById(el.getAttribute('popovertarget'));
    return pop && pop.closest('form.fi-notify');
  }

  ['pointerover', 'focusin', 'touchstart'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        warm(formFor(event.target));
      },
      { passive: true }
    );
  });

  function prepareAll(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches('form.fi-notify')) prepare(root);
    Array.prototype.forEach.call(root.querySelectorAll('form.fi-notify'), prepare);
  }

  prepareAll(document);
  document.addEventListener('DOMContentLoaded', function () {
    prepareAll(document);
  });
  /* Cards added later (filters, infinite scroll, quick view). */
  new MutationObserver(function (records) {
    records.forEach(function (record) {
      Array.prototype.forEach.call(record.addedNodes, prepareAll);
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form.matches || !form.matches('form.fi-notify')) return;
      prepare(form);
      if (form.dataset.fiNative) return;
      if (form.dataset.fiBusy) {
        event.preventDefault();
        return;
      }
      /* Refused before the captcha runs: no token is spent and nothing is posted. */
      var refused = refusal(form);
      if (refused) {
        event.preventDefault();
        event.stopImmediatePropagation();
        show(form, refused);
        return;
      }
      form.dataset.fiPending = '1';
      parts(form).note.textContent = SENDING;
      watchChallenge(form);
    },
    true
  );

  /* Last listener on the way out: if nothing (no captcha script) took the submit
     over, send it here instead of letting the page post. */
  window.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches || !form.matches('form.fi-notify') || event.defaultPrevented || form.dataset.fiNative) return;
    event.preventDefault();
    send(form);
  });
})();
